import { useState } from 'react';
import { Link2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input, Button } from '@/components/ui';
import DeeplinkCard from './DeeplinkCard';
import type { Deeplink } from '@/types';

const PAGE_SIZE = 10;

interface DeeplinkListProps {
  deeplinks: Deeplink[];
  clicksMap?: Record<string, number>;
  clicksLoading?: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onDelete: (id: string) => void;
  onToggleDisabled?: (id: string) => void;
  onCreateNew: () => void;
}

const DeeplinkList = ({
  deeplinks,
  clicksMap = {},
  clicksLoading = false,
  searchQuery,
  onSearchChange,
  onDelete,
  onToggleDisabled,
  onCreateNew,
}: DeeplinkListProps) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);

  const filteredDeeplinks = deeplinks.filter((deeplink) => {
    const query = searchQuery.toLowerCase();
    return (
      deeplink.name?.toLowerCase().includes(query) ||
      deeplink.code.toLowerCase().includes(query) ||
      deeplink.originalUrl.toLowerCase().includes(query) ||
      deeplink.platform.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredDeeplinks.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedDeeplinks = filteredDeeplinks.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const handleSearchChange = (query: string) => {
    onSearchChange(query);
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Search & Filter Bar */}
      <div className="mb-6">
        <Input
          placeholder={t('deeplinks.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          leftIcon={<Search className="w-5 h-5" />}
        />
      </div>

      {/* Deeplinks Grid */}
      {filteredDeeplinks.length > 0 ? (
        <>
          <div className="grid gap-4">
            {paginatedDeeplinks.map((deeplink) => (
              <DeeplinkCard
                key={deeplink.id}
                deeplink={deeplink}
                clicks={clicksMap[deeplink.id] ?? 0}
                clicksLoading={clicksLoading}
                onDelete={onDelete}
                onToggleDisabled={onToggleDisabled}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                leftIcon={<ChevronLeft className="w-4 h-4" />}
              >
                {t('common.previous')}
              </Button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('common.pageOf', { current: safePage, total: totalPages })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                rightIcon={<ChevronRight className="w-4 h-4" />}
              >
                {t('common.next')}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-16">
          {searchQuery ? (
            <>
              <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-dark-800 dark:text-white mb-2">{t('deeplinks.noResults')}</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {t('deeplinks.noResultsMessage', { query: searchQuery })}
              </p>
              <Button variant="ghost" onClick={() => handleSearchChange('')}>
                {t('deeplinks.clearSearch')}
              </Button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-lime-100 dark:bg-lime-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Link2 className="w-8 h-8 text-lime-600 dark:text-lime-400" />
              </div>
              <h3 className="text-lg font-medium text-dark-800 dark:text-white mb-2">{t('deeplinks.noDeeplinksYet')}</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {t('deeplinks.noDeeplinksMessage')}
              </p>
              <Button variant="primary" onClick={onCreateNew} leftIcon={<Link2 className="w-4 h-4" />}>
                {t('deeplinks.createFirstDeeplink')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DeeplinkList;
